import { z } from "zod"
import { SupportedRegions } from "@nova/constants"

export const callbackConfig = z.object({
    url: z.string(),
    apiKey: z.string().optional(),
})

export const azureCredentials = z.object({
    sas: z.string(),
})

export const s3Credentials = z.object({
    accessKey: z.string(),
    secretKey: z.string(),
    region: z.string(),
    bucket: z.string(),
})

export const venturaCredentials = z.object({
    apiKey: z.string(),
    url: z.string(),
})

export const intterraCredentials = z.object({
    accessKeyId: z.string().min(1),
    secretAccessKey: z.string().min(1),
    bucketName: z.string().min(1),
    region: z.string().optional(),
    prefix: z.string().optional(),
})

export const credentialLocator = z.object({
    secretId: z.string(),
    region: z.enum(SupportedRegions),
})

export const intterraSecretLocator = z.object({
    secretName: z.string(),
})

export const integrationAuthorizationLocator = z.object({
    workspaceId: z.string(),
    userId: z.string(),
})

export type CallbackConfig = z.infer<typeof callbackConfig>
export type CredentialLocator = z.infer<typeof credentialLocator>
export type IntegrationAuthorizationLocator = z.infer<typeof integrationAuthorizationLocator>
export type IntterraSecretLocator = z.infer<typeof intterraSecretLocator>
export type AzureCredentials = z.infer<typeof azureCredentials>
export type S3Credentials = z.infer<typeof s3Credentials>
export type VenturaCredentials = z.infer<typeof venturaCredentials>
export type IntterraCredentials = z.infer<typeof intterraCredentials>

const destinationOptions = {
    prefix: z
        .string()
        .transform((prefix) => prefix.replace(/^\/+|\/+$/g, ""))
        .optional(),
    callback: callbackConfig.optional(),
}

export const adapterConfig = z.discriminatedUnion("provider", [
    z.object({ provider: z.literal("azure"), config: credentialLocator, ...destinationOptions }),
    z.object({ provider: z.literal("s3"), config: credentialLocator, ...destinationOptions }),
    z.object({ provider: z.literal("ventura"), config: credentialLocator, ...destinationOptions }),
    z.object({
        provider: z.literal("agol"),
        config: integrationAuthorizationLocator,
        ...destinationOptions,
    }),
    z.object({
        provider: z.literal("tak"),
        config: integrationAuthorizationLocator,
        ...destinationOptions,
    }),
    z.object({
        provider: z.literal("caltopo"),
        config: integrationAuthorizationLocator,
        ...destinationOptions,
    }),
    z.object({
        provider: z.literal("intterra"),
        config: intterraSecretLocator,
        ...destinationOptions,
    }),
])

export const transferSecret = z.discriminatedUnion("provider", [
    z.object({ provider: z.literal("azure"), config: azureCredentials, ...destinationOptions }),
    z.object({ provider: z.literal("s3"), config: s3Credentials, ...destinationOptions }),
    z.object({ provider: z.literal("ventura"), config: venturaCredentials, ...destinationOptions }),
])

export type AdapterConfig = z.infer<typeof adapterConfig>
export type TransferSecret = z.infer<typeof transferSecret>
